import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import api from '../services/api';
import {
  AlertCircleIcon,
  LockIcon,
  CheckIcon
} from '../components/Icons';
import './Auth.css';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { getLocalizedPath } = useLanguage();

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setInvalidToken(true);
      setError(t('resetPassword.errors.invalidToken'));
    } else {
      setToken(tokenParam);
    }
  }, [searchParams, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate password
    if (password.length < 8) {
      setError(t('resetPassword.errors.passwordTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('resetPassword.errors.passwordMismatch'));
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: password
      });
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate(getLocalizedPath('/login'));
      }, 3000);
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string'
        ? errorDetail
        : errorDetail?.message || t('resetPassword.errors.generic');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (invalidToken) {
    return (
      <div className="auth-page">
        <header className="auth-header">
          <div className="auth-header-content">
            <div className="auth-logo" onClick={() => navigate(getLocalizedPath('/'))}>
              <img src="/logo_transp.png" alt="ContratoSeguro" />
            </div>
            <div className="auth-header-actions">
              <button onClick={() => navigate(getLocalizedPath('/login'))} className="btn-primary-auth">
                {t('resetPassword.goToLogin')}
              </button>
            </div>
          </div>
        </header>

        <main className="auth-main">
          <div className="auth-container">
            <div className="auth-grid">
              <div className="auth-form-column">
                <div className="auth-card">
                  <div className="auth-card-header">
                    <h1>{t('resetPassword.errors.invalidLinkTitle', { defaultValue: 'Invalid link' })}</h1>
                    <p>{t('resetPassword.errors.invalidLinkMessage', { defaultValue: 'This password reset link is invalid or has expired.' })}</p>
                  </div>

                  <div className="auth-error">
                    <AlertCircleIcon size={20} />
                    <span>{error}</span>
                  </div>

                  <div className="auth-form">
                    <button
                      onClick={() => navigate(getLocalizedPath('/recuperar-senha'))}
                      className="btn-primary-full"
                    >
                      {t('resetPassword.requestNewLink', { defaultValue: 'Request new link' })}
                    </button>
                    <div className="auth-secondary-action">
                      <a href={getLocalizedPath('/login')} className="link-secondary">{t('resetPassword.goToLogin')}</a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="auth-presentation-column">
                <div className="auth-presentation">
                  <h2>{t('resetPassword.securityTipTitle', { defaultValue: 'Security links expire' })}</h2>
                  <p className="presentation-subtitle">
                    {t('resetPassword.securityTipSubtitle', { defaultValue: 'For security, password reset links expire in 1 hour.' })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="auth-page">
      {/* Header */}
      <header className="auth-header">
        <div className="auth-header-content">
          <div className="auth-logo" onClick={() => navigate(getLocalizedPath('/'))}>
            <img src="/logo_transp.png" alt="ContratoSeguro" />
          </div>
          <div className="auth-header-actions">
            <button onClick={() => navigate(getLocalizedPath('/login'))} className="btn-text">
              {t('resetPassword.goToLogin')}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="auth-main">
        <div className="auth-container">
          <div className="auth-grid">
            {/* Left Column - Form */}
            <div className="auth-form-column">
              <div className="auth-card">
                <div className="auth-card-header">
                  <h1>{t('resetPassword.title')}</h1>
                  <p>{t('resetPassword.subtitle')}</p>
                </div>

                {error && (
                  <div className="auth-error">
                    <AlertCircleIcon size={20} />
                    <span>{error}</span>
                  </div>
                )}

                {success && (
                  <div className="auth-success">
                    <CheckIcon size={20} />
                    <div>
                      <strong>{t('resetPassword.success.title')}</strong>
                      <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
                        {t('resetPassword.success.message')}
                      </p>
                    </div>
                  </div>
                )}

                {!success ? (
                  <form onSubmit={handleSubmit} className="auth-form">
                    <div className="form-field">
                      <label htmlFor="password">{t('resetPassword.password')}</label>
                      <input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('resetPassword.passwordPlaceholder')}
                        required
                        minLength={8}
                        disabled={loading}
                        autoComplete="new-password"
                      />
                      <small className="form-field-hint">{t('resetPassword.passwordPlaceholder')}</small>
                    </div>

                    <div className="form-field">
                      <label htmlFor="confirmPassword">{t('resetPassword.confirmPassword')}</label>
                      <input
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                        required
                        minLength={8}
                        disabled={loading}
                        autoComplete="new-password"
                      />
                    </div>

                    <button type="submit" className="btn-primary-full" disabled={loading}>
                      {loading ? t('resetPassword.submitting') : t('resetPassword.submit')}
                    </button>

                    <div className="auth-secondary-action">
                      <a href={getLocalizedPath('/login')} className="link-secondary">{t('resetPassword.goToLogin')}</a>
                    </div>
                  </form>
                ) : (
                  <div className="auth-form">
                    <button
                      onClick={() => navigate(getLocalizedPath('/login'))}
                      className="btn-primary-full"
                    >
                      {t('resetPassword.goToLogin')}
                    </button>
                  </div>
                )}

                <div className="auth-trust-footer">
                  <LockIcon size={16} />
                  <span>{t('login.dataProtected')}</span>
                </div>
              </div>
            </div>

            {/* Right Column - Presentation */}
            <div className="auth-presentation-column">
              <div className="auth-presentation">
                <h2>{t('resetPassword.tips.title', { defaultValue: 'Create a strong password' })}</h2>
                <p className="presentation-subtitle">
                  {t('resetPassword.tips.subtitle', { defaultValue: 'A strong password protects your account and contracts. Follow these tips:' })}
                </p>

                <div className="presentation-benefits">
                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <CheckIcon size={24} />
                    </div>
                    <div className="benefit-content">
                      <h3>{t('resetPassword.tips.tip1Title', { defaultValue: 'Use at least 8 characters' })}</h3>
                      <p>{t('resetPassword.tips.tip1Desc', { defaultValue: 'The longer, the safer' })}</p>
                    </div>
                  </div>

                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <CheckIcon size={24} />
                    </div>
                    <div className="benefit-content">
                      <h3>{t('resetPassword.tips.tip2Title', { defaultValue: 'Mix letters, numbers, and symbols' })}</h3>
                      <p>{t('resetPassword.tips.tip2Desc', { defaultValue: 'Combine uppercase, lowercase, and special characters' })}</p>
                    </div>
                  </div>

                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <LockIcon size={24} />
                    </div>
                    <div className="benefit-content">
                      <h3>{t('resetPassword.tips.tip3Title', { defaultValue: 'Never reuse passwords' })}</h3>
                      <p>{t('resetPassword.tips.tip3Desc', { defaultValue: 'Use a unique password for each service' })}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
